import { lazy } from 'react';
import { keyboardRouteDefs, publicRouteDefs, standardRouteDefs } from './paths';

const DashboardPage = lazy(() => import('../../features/dashboard/pages/DashboardPage'));
const ActivityHistoryPage = lazy(() => import('../../features/auditLogs/pages/ActivityHistoryPage'));
const CampaignsPage = lazy(() => import('../../features/campaigns/pages/CampaignsPage'));
const AiStudioPage = lazy(() => import('../../features/aiStudio/pages/AiStudioPage'));
const InvoiceListPage = lazy(() => import('../../features/invoices/pages/InvoiceListPage'));
const InvoiceFormPage = lazy(() => import('../../features/invoices/pages/InvoiceFormPage'));
const InvoiceViewPage = lazy(() => import('../../features/invoices/pages/InvoiceViewPage'));
const QuotationsListPage = lazy(() => import('../../features/quotations/pages/QuotationListPage'));
const QuotationFormFeaturePage = lazy(() => import('../../features/quotations/pages/QuotationFormPage'));
const QuotationViewPage = lazy(() =>
  import('../../features/quotations/pages/QuotationSnapshotViewPage')
);
const EstimatedInvoiceListFeaturePage = lazy(() =>
  import('../../features/estimatedInvoices/pages/EstimatedInvoiceListPage')
);
const EstimatedInvoiceFormFeaturePage = lazy(() =>
  import('../../features/estimatedInvoices/pages/EstimatedInvoiceFormPage')
);
const EstimatedInvoiceViewPage = lazy(() =>
  import('../../features/estimatedInvoices/pages/EstimatedInvoiceSnapshotViewPage')
);
const ProductsPage = lazy(() => import('../../features/products/pages/ProductPage'));
const CustomersPage = lazy(() => import('../../features/customers/pages/CustomerPage'));
const TaxesPage = lazy(() => import('../../features/taxes/pages/TaxPage'));
const DocumentLayoutPage = lazy(() => import('../../features/documentLayouts/pages/DocumentLayoutPage'));
const DocumentFormLayoutPage = lazy(() =>
  import('../../features/documentFormLayouts/pages/DocumentFormLayoutPage')
);
const SettingsFeaturePage = lazy(() => import('../../features/settings/pages/SettingsPage'));
const AdministrativeSettingsPage = lazy(() =>
  import('../../features/settings/pages/AdministrativeSettingsPage')
);
const FieldControllerPage = lazy(() =>
  import('../../features/fieldController/pages/FieldControllerPage')
);
const CustomFieldsPage = lazy(() =>
  import('../../features/customFields/pages/CustomFieldsPage')
);
const LedgerFeaturePage = lazy(() => import('../../features/ledger/pages/LedgerPage'));
const LandingPage = lazy(() => import('../../features/public/pages/LandingPage'));
const ClerkSignInPage = lazy(() => import('../../features/public/pages/ClerkSignInPage'));
const AcceptInvitationPage = lazy(() => import('../../features/public/pages/AcceptInvitationPage'));
const RequestPortalPage = lazy(() => import('../../features/public/pages/RequestPortalPage'));
const CampaignUnsubscribePage = lazy(() =>
  import('../../features/public/pages/CampaignUnsubscribePage')
);
const SamplePortalPage = lazy(() => import('../../features/public/pages/SamplePortalPage'));
const CustomerPortalAuthPage = lazy(() =>
  import('../../features/customerPortal/pages/CustomerPortalAuthPage')
);
const LoginPage = lazy(() => import('../../features/auth/pages/LoginPage'));
const AdminLoginPage = lazy(() => import('../../features/admin/pages/AdminLoginPage'));
const AdminConsolePage = lazy(() => import('../../features/admin/pages/AdminConsolePage'));
const AdminBugControlPage = lazy(() => import('../../features/admin/pages/AdminBugControlPage'));
const CustomerPortalHomePage = lazy(() =>
  import('../../features/customerPortal/pages/CustomerPortalHomePage')
);
const CustomerPortalDocumentsPage = lazy(() =>
  import('../../features/customerPortal/pages/CustomerPortalDocumentsPage')
);
const CustomerPortalDocumentDetailPage = lazy(() =>
  import('../../features/customerPortal/pages/CustomerPortalDocumentDetailPage')
);
const CustomerPortalPlannerPage = lazy(() =>
  import('../../features/customerPortal/pages/CustomerPortalPlannerPage')
);

const MainMenu = lazy(() => import('../../pages/MainMenu/MainMenu'));
const PageShell = lazy(() => import('../../pages/MainMenu/PageShell'));
const KeyboardSettingsPage = lazy(() => import('../../features/settings/pages/KeyboardSettingsPage'));
const KeyboardFieldControllerPage = lazy(() =>
  import('../../features/fieldController/pages/KeyboardFieldControllerPage')
);
const KeyboardCustomFieldsPage = lazy(() =>
  import('../../features/customFields/pages/KeyboardCustomFieldsPage')
);
const TaxPage = lazy(() => import('../../features/taxes/pages/KeyboardTaxPage'));
const CustomerPage = lazy(() => import('../../features/customers/pages/KeyboardCustomerPage'));
const ProductPage = lazy(() => import('../../features/products/pages/KeyboardProductPage'));
const KeyboardLedgerPage = lazy(() => import('../../features/ledger/pages/KeyboardLedgerPage'));
const InvoicePage = lazy(() => import('../../features/invoices/pages/KeyboardInvoicePage'));
const InvoiceActionPage = lazy(() => import('../../features/invoices/pages/KeyboardInvoiceActionPage'));
const KeyboardQuotationPage = lazy(() => import('../../features/quotations/pages/KeyboardQuotationPage'));
const KeyboardEstimatedInvoicePage = lazy(() =>
  import('../../features/estimatedInvoices/pages/KeyboardEstimatedInvoicePage')
);
const KeyboardInvoiceFormPage = lazy(() => import('../../features/invoices/pages/KeyboardInvoiceFormPage'));
const KeyboardInvoiceViewPage = lazy(() => import('../../features/invoices/pages/KeyboardInvoiceViewPage'));
const KeyboardQuotationFormFeaturePage = lazy(() =>
  import('../../features/quotations/pages/KeyboardQuotationFormPage')
);
const KeyboardQuotationSnapshotViewPage = lazy(() =>
  import('../../features/quotations/pages/KeyboardQuotationSnapshotViewPage')
);
const KeyboardEstimatedInvoiceFormFeaturePage = lazy(() =>
  import('../../features/estimatedInvoices/pages/KeyboardEstimatedInvoiceFormPage')
);
const KeyboardEstimatedInvoiceViewPage = lazy(() =>
  import('../../features/estimatedInvoices/pages/KeyboardEstimatedInvoiceSnapshotViewPage')
);

const componentRegistry = {
  dashboard: DashboardPage,
  activityHistory: ActivityHistoryPage,
  campaigns: CampaignsPage,
  aiStudioCampaigns: AiStudioPage,
  aiStudioSalesDocuments: AiStudioPage,
  aiStudioDocumentStudio: AiStudioPage,
  invoiceList: InvoiceListPage,
  invoiceForm: InvoiceFormPage,
  invoiceView: InvoiceViewPage,
  quotationList: QuotationsListPage,
  quotationForm: QuotationFormFeaturePage,
  quotationView: QuotationViewPage,
  estimatedInvoiceList: EstimatedInvoiceListFeaturePage,
  estimatedInvoiceForm: EstimatedInvoiceFormFeaturePage,
  estimatedInvoiceView: EstimatedInvoiceViewPage,
  products: ProductsPage,
  customers: CustomersPage,
  taxes: TaxesPage,
  documentLayouts: DocumentLayoutPage,
  documentFormLayouts: DocumentFormLayoutPage,
  settings: SettingsFeaturePage,
  settingsAdmin: AdministrativeSettingsPage,
  fieldController: FieldControllerPage,
  customFields: CustomFieldsPage,
  ledger: LedgerFeaturePage,
  landing: LandingPage,
  clerkSignIn: ClerkSignInPage,
  acceptInvitation: AcceptInvitationPage,
  requestPortal: RequestPortalPage,
  campaignUnsubscribe: CampaignUnsubscribePage,
  samplePortal: SamplePortalPage,
  customerPortalAuth: CustomerPortalAuthPage,
  login: LoginPage,
  adminLogin: AdminLoginPage,
  adminConsole: AdminConsolePage,
  adminBugs: AdminBugControlPage,
  customerPortalHome: CustomerPortalHomePage,
  customerPortalDocuments: CustomerPortalDocumentsPage,
  customerPortalDocumentDetail: CustomerPortalDocumentDetailPage,
  customerPortalPlanner: CustomerPortalPlannerPage,

  mainMenu: MainMenu,
  pageShell: PageShell,
  keyboardSettings: KeyboardSettingsPage,
  keyboardFieldController: KeyboardFieldControllerPage,
  keyboardCustomFields: KeyboardCustomFieldsPage,
  keyboardTaxes: TaxPage,
  keyboardCustomers: CustomerPage,
  keyboardProducts: ProductPage,
  keyboardLedger: KeyboardLedgerPage,
  keyboardCampaigns: CampaignsPage,
  keyboardInvoiceList: InvoicePage,
  keyboardInvoiceForm: KeyboardInvoiceFormPage,
  keyboardInvoiceView: KeyboardInvoiceViewPage,
  keyboardInvoiceActions: InvoiceActionPage,
  keyboardQuotationList: KeyboardQuotationPage,
  keyboardQuotationForm: KeyboardQuotationFormFeaturePage,
  keyboardQuotationView: KeyboardQuotationSnapshotViewPage,
  keyboardEstimatedInvoiceList: KeyboardEstimatedInvoicePage,
  keyboardEstimatedInvoiceForm: KeyboardEstimatedInvoiceFormFeaturePage,
  keyboardEstimatedInvoiceView: KeyboardEstimatedInvoiceViewPage,
};

export const standardRoutes = standardRouteDefs.map((route) => ({
  ...route,
  element: componentRegistry[route.key],
}));

export const publicRoutes = publicRouteDefs.map((route) => ({
  ...route,
  element: componentRegistry[route.key],
}));

export const keyboardRoutes = keyboardRouteDefs.map((route) => ({
  ...route,
  element: componentRegistry[route.key],
}));
